/**
 * Opening-hours exceptions (#1282)
 *
 * Per-date overrides of the weekly opening hours — holidays, shortened days,
 * exceptional openings. Each row is its own resource, unlike the JSON array in
 * tenant settings it replaces: two people editing the workshop settings no
 * longer overwrite one another's exceptions.
 *
 * Writes are immediate, not batched behind the settings page's Save button —
 * the rows are a separate resource with their own endpoints, so there is no
 * pending form state to lose.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { HoursException } from '@/types';

interface HoursExceptionsResponse {
  exceptions: HoursException[];
}

/** Create/update payload — `id` is server-owned, the rest mirrors the row. */
export type HoursExceptionInput = Omit<HoursException, 'id'>;

export function useHoursExceptions(range?: { from?: string; to?: string }) {
  const query = new URLSearchParams();
  if (range?.from) query.set('from', range.from);
  if (range?.to) query.set('to', range.to);
  const suffix = query.size > 0 ? `?${query.toString()}` : '';

  const result = useQuery({
    queryKey: queryKeys.hoursExceptions.list(range?.from, range?.to),
    queryFn: () => api.get<HoursExceptionsResponse>(`/hours-exceptions${suffix}`),
    // Holidays change a few times a year; no need to refetch aggressively.
    staleTime: 5 * 60 * 1000,
  });

  return { ...result, exceptions: result.data?.exceptions ?? [] };
}

/**
 * Invalidates both the exception list and the tenant settings query, because
 * the calendar resolves opening hours from the two together.
 */
function useInvalidateHoursExceptions() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.hoursExceptions.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.tenant.settings() });
  };
}

export function useCreateHoursException() {
  const invalidate = useInvalidateHoursExceptions();
  return useMutation({
    mutationFn: (input: HoursExceptionInput) =>
      api.post<{ exception: HoursException }>('/hours-exceptions', input),
    onSuccess: invalidate,
  });
}

export function useUpdateHoursException() {
  const invalidate = useInvalidateHoursExceptions();
  return useMutation({
    mutationFn: ({ id, ...input }: HoursException) =>
      api.put<{ exception: HoursException }>(`/hours-exceptions/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteHoursException() {
  const invalidate = useInvalidateHoursExceptions();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/hours-exceptions/${id}`),
    onSuccess: invalidate,
  });
}
